import { getCartErrorMessage } from '@theme/cart-feedback';

class SProductForm extends HTMLElement {
  #ac;
  #abortController;
  #activeSubmitter;
  #activeSubmitterAriaDisabled;
  #activeSubmitterHadOpacityClass;
  #activeSubmitterHadCursorWaitClass;
  #atcTimer = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('submit', (event) => {
      if (!this.#shouldHandleSubmit(event)) return;

      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;

      this.#submit(event.target, event.submitter);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#abortController?.abort();
    this.#setSubmitting(false);
  }

  #shouldHandleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return false;

    // If Standard Actions are unavailable, leave the Shopify product form native.
    if (!window.Shopify?.actions?.updateCart) return false;

    if (!this.#variantInput(form)) return false;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"], input[name="add"]')) {
      return false;
    }

    return true;
  }

  async #submit(form, submitter) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const formData = new FormData(form);
    const line = this.#lineFromFormData(formData);
    if (!line) {
      this.#abortController = null;
      return;
    }

    this.#setSubmitting(true, submitter);
    this.#setAtcState(submitter, 'busy');
    this.#showError('');
    // The flight runs alongside the request, so the wait reads as motion, not lag.
    const flight = this.#flyToCart();

    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: this.#withBundle(line, formData) },
        {
          event: {
            context: 'product',
            detail: this.#eventDetail(),
          },
          signal,
        }
      );

      this.#throwIfAborted(signal);

      const errorMessage = this.#resultErrorMessage(result);
      if (errorMessage) {
        flight.cancel();
        this.#setAtcState(submitter, null);
        this.#showError(errorMessage);
      } else {
        await flight.done;
        this.#bumpCartIcon();
        this.#setAtcState(submitter, 'done', 1800);
        await window.Shopify.actions.openCart?.();
        this.#highlightLine(line.merchandiseId);
      }
    } catch (error) {
      flight.cancel();
      this.#setAtcState(submitter, null);
      if (signal.aborted) return;

      this.#showError(this.#cleanMessage(getCartErrorMessage(error)) || this.dataset.errorMessage || '');
    } finally {
      if (!signal.aborted) {
        this.#setSubmitting(false);
        if (this.#abortController?.signal === signal) this.#abortController = null;
      }
    }
  }

  /** Platform errors can arrive without a message; never show "undefined"/"null" to buyers. */
  #cleanMessage(message) {
    const text = message == null ? '' : String(message).trim();
    return /^(undefined|null|\[object object\])$/i.test(text) ? '' : text;
  }

  #setAtcState(button, state, resetAfter = 0) {
    if (!(button instanceof HTMLElement) || !button.hasAttribute('data-atc')) return;
    clearTimeout(this.#atcTimer);
    if (state) button.setAttribute('data-atc-state', state);
    else button.removeAttribute('data-atc-state');
    if (resetAfter) {
      this.#atcTimer = setTimeout(() => button.removeAttribute('data-atc-state'), resetAfter);
    }
  }

  #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** Clone the visible product image and arc it into the header cart icon. */
  #flyToCart() {
    const idle = { done: Promise.resolve(), cancel() {} };
    if (this.#reducedMotion()) return idle;

    const images = [...document.querySelectorAll('[data-product-gallery] img')];
    const source = images.find((img) => {
      const r = img.getBoundingClientRect();
      return r.width > 40 && r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth;
    }) || images[0];
    if (!source) return idle;

    const from = source.getBoundingClientRect();
    const icon = document.getElementById('header-cart-trigger');
    let to = icon?.getBoundingClientRect();
    if (!to || to.width === 0 || to.bottom < 0) {
      to = { left: window.innerWidth - 56, top: 16, width: 32, height: 32 };
    }

    const size = Math.min(160, Math.max(96, Math.min(from.width, from.height) * 0.4));
    const startX = from.left + from.width / 2 - size / 2;
    const startY = Math.max(16, Math.min(from.top + from.height / 2, window.innerHeight - size) - size / 2);
    const endX = to.left + to.width / 2 - size / 2;
    const endY = to.top + to.height / 2 - size / 2;
    const midX = startX + (endX - startX) * 0.55;
    const midY = Math.min(startY, endY) - 90;

    const ghost = document.createElement('img');
    ghost.src = source.currentSrc || source.src;
    ghost.alt = '';
    ghost.setAttribute('aria-hidden', 'true');
    ghost.className = 'atc-ghost';
    ghost.style.width = `${size}px`;
    ghost.style.height = `${size}px`;
    document.body.appendChild(ghost);

    const animation = ghost.animate([
      { transform: `translate(${startX}px, ${startY}px) scale(0.6)`, opacity: 0, borderRadius: '24px' },
      { transform: `translate(${startX}px, ${startY - 12}px) scale(1)`, opacity: 1, borderRadius: '24px', offset: 0.18 },
      { transform: `translate(${midX}px, ${midY}px) scale(0.62) rotate(-8deg)`, opacity: 1, borderRadius: '40px', offset: 0.62 },
      { transform: `translate(${endX}px, ${endY}px) scale(0.12) rotate(0deg)`, opacity: 0.2, borderRadius: '999px' },
    ], { duration: 820, easing: 'cubic-bezier(0.45, 0, 0.2, 1)', fill: 'forwards' });

    const done = animation.finished.catch(() => {}).finally(() => ghost.remove());
    return {
      done,
      cancel() {
        animation.cancel();
        ghost.remove();
      },
    };
  }

  #bumpCartIcon() {
    const icon = document.getElementById('header-cart-trigger');
    if (!icon || this.#reducedMotion()) return;
    icon.animate(
      [{ transform: 'scale(1)' }, { transform: 'scale(1.28)' }, { transform: 'scale(0.92)' }, { transform: 'scale(1)' }],
      { duration: 520, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }
    );
  }

  /** Mark the drawer row of the variant just added so it glows once. */
  #highlightLine(variantId) {
    const id = String(variantId).split('/').pop();
    const row = document.querySelector(`#cart-drawer [data-variant-id="${CSS.escape(id)}"]`)?.closest('.cart-glass-row');
    if (!row) return;
    row.removeAttribute('data-just-added');
    void row.offsetWidth;
    row.setAttribute('data-just-added', '');
    // Scroll only the drawer body vertically: scrollIntoView would also scroll the clipped pane sideways.
    const scroller = row.closest('.overflow-y-auto');
    if (scroller) {
      const top = row.offsetTop - scroller.offsetTop - 16;
      if (top < scroller.scrollTop || top + row.offsetHeight > scroller.scrollTop + scroller.clientHeight) {
        scroller.scrollTo({ top, behavior: this.#reducedMotion() ? 'auto' : 'smooth' });
      }
    }
    setTimeout(() => row.removeAttribute('data-just-added'), 2200);
  }

  #variantInput(form) {
    const input = form.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input : null;
  }

  /** An optional bundle checkbox (name="bundle_id") adds one more line, e.g. the Support Package. */
  #withBundle(line, formData) {
    const bundleId = String(formData.get('bundle_id') || '').trim();
    const bundleQty = Math.max(1, Number.parseInt(String(formData.get('bundle_qty') || '1'), 10) || 1);
    return bundleId ? [line, { merchandiseId: bundleId, quantity: bundleQty }] : [line];
  }

  #lineFromFormData(formData) {
    const variantId = String(formData.get('id') || '').trim();
    if (!variantId) return null;

    const quantityValue = Number.parseInt(String(formData.get('quantity') || '1'), 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const line = {
      merchandiseId: variantId,
      quantity,
    };

    const sellingPlanId = String(formData.get('selling_plan') || '').trim();
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    const attributes = this.#lineAttributes(formData);
    if (attributes.length) line.attributes = attributes;

    return line;
  }

  #lineAttributes(formData) {
    const attributes = [];

    for (const [name, value] of formData) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match) continue;
      if (typeof File !== 'undefined' && value instanceof File) continue;

      const attributeValue = String(value).trim();
      if (!attributeValue) continue;

      attributes.push({ key: match[1], value: attributeValue });
    }

    return attributes;
  }

  #eventDetail() {
    const detail = { source: 's-product-form' };
    if (this.dataset.productId) detail.productId = this.dataset.productId;
    return detail;
  }

  #resultErrorMessage(result) {
    return this.#cleanMessage(result?.userErrors?.[0]?.message);
  }

  #showError(message) {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
  }

  #setSubmitting(submitting, submitter = this.#activeSubmitter) {
    if (submitting) {
      this.setAttribute('aria-busy', 'true');
      if (submitter instanceof HTMLElement) {
        this.#activeSubmitter = submitter;
        this.#activeSubmitterAriaDisabled = submitter.getAttribute('aria-disabled');
        this.#activeSubmitterHadOpacityClass = submitter.classList.contains('opacity-50');
        this.#activeSubmitterHadCursorWaitClass = submitter.classList.contains('cursor-wait');
        submitter.setAttribute('aria-disabled', 'true');
        submitter.classList.add(...(submitter.hasAttribute('data-atc') ? ['cursor-wait'] : ['opacity-50', 'cursor-wait']));
      }
      return;
    }

    this.removeAttribute('aria-busy');
    if (this.#activeSubmitter instanceof HTMLElement) {
      if (this.#activeSubmitterAriaDisabled == null) {
        this.#activeSubmitter.removeAttribute('aria-disabled');
      } else {
        this.#activeSubmitter.setAttribute('aria-disabled', this.#activeSubmitterAriaDisabled);
      }
      if (!this.#activeSubmitterHadOpacityClass) this.#activeSubmitter.classList.remove('opacity-50');
      if (!this.#activeSubmitterHadCursorWaitClass) this.#activeSubmitter.classList.remove('cursor-wait');
    }
    this.#activeSubmitter = null;
    this.#activeSubmitterAriaDisabled = null;
    this.#activeSubmitterHadOpacityClass = null;
    this.#activeSubmitterHadCursorWaitClass = null;
  }

  #throwIfAborted(signal) {
    if (!signal.aborted) return;

    if (typeof signal.throwIfAborted === 'function') {
      signal.throwIfAborted();
    }

    throw new DOMException('Aborted', 'AbortError');
  }
}

if (!customElements.get('s-product-form')) {
  customElements.define('s-product-form', SProductForm);
}
